import { CSSProperties, forwardRef, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { IFormField } from '@vivli/shared/infrastructure/interface';
import { BaseInputFieldComponent } from './base-input-field.component';
import { FormFieldTypeEnum } from '@vivli/shared/infrastructure/enum';
import mergeRefs from 'react-merge-refs';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import { Color, Size } from '@vivli/shared/theme';

const inputStyle: CSSProperties = {
    paddingTop: '10px',
    border: 'none',
    fontSize: Size.FontSize.Large,
    color: Color.DARK_GRAY,
    flex: '1 1 auto',
    resize: 'none',
    borderWidth: 'initial',
    borderStyle: 'none',
    borderColor: 'initial',
    paddingLeft: '15px',
    paddingRight: '15px',
    paddingBottom: '10px',
    backgroundColor: 'rgba(0, 0, 0, 0)',
    width: '100%',
    outline: 'none',
};

interface DatePickerComponentProps extends IFormField {
    defaultValue?: Date;
    showMonthYearPicker?: boolean;
    dateFormat?: string;
    selected?: moment.Moment;
    placeholderText?: string;
    optClassName?: string;
    maxDate?: Date;
    minDate?: Date;
    isClearable?: boolean;
    onChange?: (date: Date) => void;
    onChangeRaw?: (event?: any) => void;
}

const CustomInput = forwardRef(({ value, onClick, className, onChange, onBlur, onKeyDown, readonly }: any, ref: any) => (
    <input
        onChange={onChange}
        onBlur={onBlur}
        type={'text'}
        // onFocus={onFocus}
        style={inputStyle}
        ref={ref}
        value={value}
        onClick={onClick}
        className={className}
        // data-test-id={dataId || name}
        onKeyDown={onKeyDown}
        disabled={readonly}
    />
));

export const DatePickerComponent = forwardRef((props: DatePickerComponentProps, ref) => {
    const dateRef = useRef<HTMLDivElement>();
    const {
        defaultValue,
        value,
        onChange,
        onChangeRaw,
        showMonthYearPicker,
        dateFormat,
        placeholderText,
        selected,
        maxDate,
        minDate,
        isClearable,
        optClassName = '',
        readonly,
    } = props;

    const buildInput = (props) => {
        const selectedValue = props.value || defaultValue;
        const datePickerDate = selectedValue && new Date(selectedValue);
        const placeholderToUse = placeholderText ?? 'DD/MM/YY';
        const classes = `date-picker-component ${optClassName}`;

        return (
            <div ref={mergeRefs([dateRef, ref])} className={classes}>
                <DatePicker
                    onChange={onChange}
                    onChangeRaw={onChangeRaw}
                    placeholderText={placeholderToUse}
                    selected={datePickerDate}
                    showMonthYearPicker={showMonthYearPicker}
                    dateFormat={dateFormat}
                    maxDate={maxDate}
                    minDate={minDate}
                    isClearable={isClearable}
                    readOnly={readonly}
                    disabled={readonly}
                    customInput={<CustomInput {...props} />}
                ></DatePicker>
            </div>
        );
    };

    // prefer controlled value if it exists, then default
    const inputDisplay = value !== undefined ? buildInput({ value, readonly }) : buildInput({ defaultValue, readonly });

    return (
        <BaseInputFieldComponent {...props} type={FormFieldTypeEnum.Date} inputRef={dateRef}>
            <div>{inputDisplay}</div>
        </BaseInputFieldComponent>
    );
});
